import type { LocationIconName } from '@/lib/location-icons';

// B2B content model for the "For Business" journey (task-1269).
//
// Source: Cris's OKTshirt 12-month sales blueprint (25 Aug 2026), website-relevant
// sections only — industry pages, flagship packages with guide prices, quote-led
// calls to action and proof placement. Schools and paid advertising are
// deliberately excluded. Every capability, price and turnaround below is
// already published elsewhere on the site (product catalogue, /embroidery,
// homepage FAQ) or stated in the blueprint; nothing is invented.

export const B2B_BASE_URL = 'https://oktshirt.co.uk';
export const B2B_HUB_PATH = '/branded-workwear';
export const B2B_PACKAGES_PATH = '/business-clothing-packages';
export const CLOTHING_BRAND_PATH = '/clothing-brand-setup';

export interface B2bFaq {
  question: string;
  answer: string;
}

export interface B2bProcessStep {
  number: string;
  title: string;
  description: string;
}

export interface B2bPackage {
  slug: string;
  name: string;
  audience: string;
  includes: string[];
  /** Guide price from the blueprint. Final price is always confirmed on quote. */
  guidePrice: string;
  purpose: string;
  icon: LocationIconName;
  /** Option pre-selected in the quote form's "What do you need?" field. */
  quoteGarmentType: string;
  /** Where the pack should send people for more detail (industry page or pillar). */
  learnMoreHref?: string;
  learnMoreLabel?: string;
}

export interface B2bPainPoint {
  title: string;
  body: string;
}

export interface B2bGarment {
  handle: string;
  note: string;
}

export type B2bProof =
  | {
      type: 'case-study';
      slug: string;
      label: string;
    }
  | {
      type: 'review';
      quote: string;
      author: string;
      source: string;
      label: string;
    };

export interface B2bIndustry {
  slug: string;
  name: string;
  navLabel: string;
  icon: LocationIconName;
  headline: string;
  subheadline: string;
  metaTitle: string;
  metaDescription: string;
  /** Option pre-selected in the quote form when arriving from this page. */
  quoteGarmentType: string;
  keywords: string[];
  intro: string;
  audience: string;
  painPointsIntro: string;
  painPoints: B2bPainPoint[];
  garmentsIntro: string;
  garments: B2bGarment[];
  collectionHref: string;
  collectionLabel: string;
  packageSlugs: string[];
  decoration: { title: string; points: string[] }[];
  proof: B2bProof;
  faqs: B2bFaq[];
  includeBanners?: boolean;
}

export const b2bPackages: B2bPackage[] = [
  {
    slug: 'starter-workwear-pack',
    quoteGarmentType: 'Staff Uniform / Workwear Package',
    name: 'Starter Workwear Pack',
    audience: 'Trades, cleaners, mechanics and small shops',
    includes: [
      '5 polo shirts',
      '5 t-shirts',
      '2 hoodies',
      'Logo setup',
      'Left-chest embroidery or print',
    ],
    guidePrice: '£249–£399',
    purpose: 'The quickest way to get a small team looking like one business.',
    icon: 'workwear',
    learnMoreHref: '/workwear-for-builders-and-trades',
    learnMoreLabel: 'Workwear for trades',
  },
  {
    slug: 'trade-team-uniform-pack',
    quoteGarmentType: 'Staff Uniform / Workwear Package',
    name: 'Trade Team Uniform Pack',
    audience: 'Builders, plumbers, electricians and roofers',
    includes: [
      '10 polo shirts',
      '10 hoodies or sweatshirts',
      '5 hi-vis garments',
      'Optional caps',
      'Artwork kept on file for new starters',
    ],
    guidePrice: '£699–£1,250',
    purpose: 'Site-ready uniform for the whole crew, with easy top-ups as the team grows.',
    icon: 'automotive',
    learnMoreHref: '/workwear-for-builders-and-trades',
    learnMoreLabel: 'Workwear for trades',
  },
  {
    slug: 'premium-staff-uniform-pack',
    quoteGarmentType: 'Staff Uniform / Workwear Package',
    name: 'Premium Staff Uniform Pack',
    audience: 'Clinics, salons, estate agents and offices',
    includes: [
      'Embroidered polo shirts',
      'Tunics',
      'Softshell jackets',
      'Aprons',
      'Caps',
    ],
    guidePrice: '£500–£1,500',
    purpose: 'A smarter, consistent look for client-facing teams.',
    icon: 'corporate',
    learnMoreHref: '/embroidered-uniforms-for-clinics-and-salons',
    learnMoreLabel: 'Uniforms for clinics and salons',
  },
  {
    slug: 'sports-club-kit-pack',
    quoteGarmentType: 'Staff Uniform / Workwear Package',
    name: 'Sports Club Kit Pack',
    audience: 'Football clubs, bowls clubs, gyms and teams',
    includes: [
      'Training tops',
      'Polo shirts',
      'Hoodies',
      'Caps',
      'Banners for match days and events',
    ],
    guidePrice: '£750–£3,000',
    purpose: 'Team kit and supporter merchandise under one identity.',
    icon: 'sports',
    learnMoreHref: '/gym-and-sports-club-merchandise',
    learnMoreLabel: 'Gym and club merchandise',
  },
  {
    slug: 'event-merchandise-pack',
    quoteGarmentType: 'T-Shirts',
    name: 'Event Merchandise Pack',
    audience: 'Churches, charities, conferences, stag and hen groups',
    includes: [
      'Printed t-shirts',
      'Hoodies',
      'Caps',
      'Tote bags',
      'PVC banner',
    ],
    guidePrice: '£300–£2,000',
    purpose: 'Everything for a fixed-date event, produced to the deadline.',
    icon: 'event',
    learnMoreHref: '/event-t-shirts-and-merchandise',
    learnMoreLabel: 'Event t-shirts and merchandise',
  },
  {
    slug: 'brand-launch-production-pack',
    quoteGarmentType: 'Clothing Brand / Bulk Print Run',
    name: 'Brand Launch Production Pack',
    audience: 'Clothing brands and streetwear founders',
    includes: [
      'Sample set',
      'Bulk print run',
      'Neck labels and packaging on request',
      'Artwork on file for reorders',
    ],
    guidePrice: '£750–£5,000',
    purpose: 'From first samples to your first stock run.',
    icon: 'retail',
    learnMoreHref: CLOTHING_BRAND_PATH,
    learnMoreLabel: 'Clothing brand setup',
  },
  {
    slug: 'business-visibility-pack',
    quoteGarmentType: 'Staff Uniform / Workwear Package',
    name: 'Business Visibility Pack',
    audience: 'Local businesses',
    includes: [
      'Staff uniform',
      'PVC banner',
      'Caps or tote bags',
      'One artwork setup across everything',
    ],
    guidePrice: '£500–£2,500',
    purpose: 'Uniform and signage from one supplier, so the branding matches.',
    icon: 'community',
    learnMoreHref: '/corporate-uniforms-and-branded-merchandise',
    learnMoreLabel: 'Corporate uniforms and merchandise',
  },
];

export function getPackageBySlug(slug: string): B2bPackage | undefined {
  return b2bPackages.find((pack) => pack.slug === slug);
}

export const b2bProcess: B2bProcessStep[] = [
  {
    number: '01',
    title: 'Send your logo and rough quantities',
    description:
      'Email, WhatsApp or the quote form. Any file we can see clearly is enough to start — we will tell you if it needs redrawing.',
  },
  {
    number: '02',
    title: 'Quote within 24 hours',
    description:
      'We confirm garments, decoration method, positions and the bulk tier your quantity unlocks. No obligation.',
  },
  {
    number: '03',
    title: 'Approve the mock-up',
    description:
      'A free digital mock-up shows placement and size. Physical samples are available at cost for larger orders.',
  },
  {
    number: '04',
    title: 'Production, then collect or deliver',
    description:
      'Printed and embroidered in-house in Birmingham, usually within 3–5 working days. Free collection from Great Barr or UK-wide delivery. Your artwork stays on file for reorders.',
  },
];

export const b2bComparison: { point: string; online: string; oktshirt: string }[] = [
  {
    point: 'Who prints it',
    online: 'A third-party facility you never meet.',
    oktshirt: 'Our own DTG, DTF and embroidery machines in Great Barr, Birmingham.',
  },
  {
    point: 'Samples',
    online: 'Order online and wait for the post.',
    oktshirt: 'See garments and stitch quality in the shop, or approve a mock-up the same day.',
  },
  {
    point: 'Getting the method right',
    online: 'You choose from a drop-down.',
    oktshirt: 'We advise embroidery, DTG, DTF or screen print per garment and artwork.',
  },
  {
    point: 'Reorders',
    online: 'Start again from scratch.',
    oktshirt: 'Logo and stitch files stay on file — top up sizes for new starters by email or WhatsApp.',
  },
  {
    point: 'Deadlines',
    online: 'Fixed production windows.',
    oktshirt: 'Typical 3–5 working days, with express options and free local collection.',
  },
  {
    point: 'Accountability',
    online: 'A ticket queue.',
    oktshirt: 'A Birmingham shop with a phone number, a 2023 West Midlands supplier award and 5-star Google reviews.',
  },
];

export const b2bHubFaqs: B2bFaq[] = [
  {
    question: 'How does bulk pricing work for business orders?',
    answer:
      'Bulk pricing starts from 10 units and improves at 25, 50 and 100+ units. Quotes are priced per garment, decoration method and number of positions, so the same logo on 30 polos costs less per shirt than on 5. Repeat accounts receive preferential pricing.',
  },
  {
    question: 'Is there a minimum order for workwear or uniforms?',
    answer:
      'No. We will embroider a single polo for a new starter as readily as a run of three hundred. The only fixed cost on a first embroidery order is the one-off digitising charge (£25 front, £30 back), which is why the packages above bundle logo setup in.',
  },
  {
    question: 'What is the difference between the guide prices and my quote?',
    answer:
      'Guide prices show the typical range for each pack so you can plan a budget quickly. Your quote confirms the exact figure once we know the garments, colours, quantities, decoration positions and artwork. It is free and there is no obligation.',
  },
  {
    question: 'Embroidery or print for staff uniform?',
    answer:
      'Embroidery for polos, fleeces, softshells, tunics and caps — it survives hundreds of washes and looks premium. Print (DTG or DTF) for t-shirts, hoodies and large, colourful or photographic artwork. Most uniform orders mix both, and we will recommend per garment.',
  },
  {
    question: 'How quickly can you turn a business order around?',
    answer:
      'Most orders are produced within 3–5 working days from artwork approval. First embroidery orders need a little longer for digitising and a sew-out check. Tell us your deadline and we will confirm a realistic date before you commit; express and same-day options exist for urgent jobs.',
  },
  {
    question: 'Can you invoice and set up an account for repeat orders?',
    answer:
      'Yes. Once your artwork is on file, reorders are a quick message with sizes and quantities. We can set up an approved-supplier account for businesses and organisations that reorder regularly.',
  },
  {
    question: 'Do you deliver outside Birmingham?',
    answer:
      'Yes. Everything is produced at 126 Beeches Road, Great Barr, and delivered to any UK postcode. Local businesses can collect free of charge, usually within 24 hours of the order being ready.',
  },
  {
    question: 'What artwork do you need?',
    answer:
      'Vector files (AI, EPS, PDF, SVG) are ideal; high-resolution PNG or JPG works for most logos. If you only have a photo of an old garment or a low-resolution file, our design team can redraw it for a small fee before production.',
  },
];

const tradesReview: B2bProof = {
  type: 'review',
  quote:
    'BAT Building Services Gas and Plumbing printed the t-shirt in Great Barr at OK T-shirt Printing and embroidery shop. Chris did a great job for my company and the printed t-shirt came exactly as I wanted.',
  author: 'Barrington Taylor',
  source: 'Google review',
  label: 'Trade customer, Great Barr',
};

const deadlineReview: B2bProof = {
  type: 'review',
  quote:
    'I put in a last minute order and then contacted the store saying it was a gift and I needed it ASAP and they were so accommodating. The people that run this shop are lovely, professional and efficient. Absolutely amazing product, I will be back!',
  author: 'Emma Calveley',
  source: 'Google review',
  label: 'Last-minute order',
};

export const b2bIndustries: B2bIndustry[] = [
  {
    slug: 'workwear-for-builders-and-trades',
    quoteGarmentType: 'Staff Uniform / Workwear Package',
    name: 'Construction & Trades',
    navLabel: 'Construction & Trades',
    icon: 'workwear',
    headline: 'Workwear for Builders & Trades',
    subheadline:
      'Embroidered and printed polos, hoodies, hi-vis and jackets for site teams — produced in-house in Birmingham and delivered UK-wide.',
    metaTitle: 'Workwear for Builders & Trades Birmingham | Branded Site Uniform | OK T-Shirt',
    metaDescription:
      'Branded workwear for builders, plumbers, electricians and roofers. Embroidered polos, printed hoodies, hi-vis and softshells from a Birmingham workshop, with artwork on file for new starters. Free quote.',
    keywords: [
      'workwear for builders',
      'branded workwear Birmingham',
      'embroidered workwear for tradesmen',
      'trade team uniform',
      'printed hi-vis with logo',
      'construction company workwear UK',
    ],
    intro:
      'A crew in matching, logoed workwear looks like a business customers can trust. We set your logo up once, then keep it on file so replacements and new starters take a message, not a meeting.',
    audience: 'Builders, roofers, plumbers, electricians, decorators, landscapers, plasterers and security firms',
    painPointsIntro: 'The jobs trades ask us to solve most often.',
    painPoints: [
      {
        title: 'The team looks unprofessional on site',
        body: 'Matching embroidered polos and printed hoodies turn a group of subcontractors into one visible company.',
      },
      {
        title: 'Damaged workwear needs replacing fast',
        body: 'Your logo stays on file, so a replacement polo or hoodie is a quick reorder rather than a new setup.',
      },
      {
        title: 'New starters need kit this week',
        body: 'Small top-up orders are welcome — no minimum, same stitch quality as the original run.',
      },
      {
        title: 'Hi-vis has to carry the company name',
        body: 'Printed and embroidered hi-vis keeps the team compliant and identifiable at a glance.',
      },
    ],
    garmentsIntro: 'Hard-wearing garments trades order most, priced per unit with bulk pricing from 10.',
    garments: [
      { handle: 'custom-embroidered-polo-t-shirt', note: 'The everyday site polo' },
      { handle: 'custom-printed-hoodie-80-20', note: 'Warm layer with back print' },
      { handle: 'custom-embroidered-hi-vis-zip-hoodie', note: 'Compliance and branding together' },
      { handle: 'custom-embroidered-softshell-jacket', note: 'Winter and van-to-site jacket' },
      { handle: 'custom-embroidered-baseball-cap', note: 'Stitched on a cap frame' },
      { handle: 'custom-printed-round-neck-t-shirt', note: 'Summer site tee' },
    ],
    collectionHref: '/collections/workwear',
    collectionLabel: 'View all workwear',
    packageSlugs: ['trade-team-uniform-pack', 'starter-workwear-pack'],
    decoration: [
      {
        title: 'Embroider',
        points: ['Polos, softshells and caps', 'Left-chest company logo', 'Survives site washing'],
      },
      {
        title: 'Print',
        points: ['Hoodies and t-shirts', 'Large back prints with phone number and trade', 'Hi-vis vests and jackets'],
      },
    ],
    proof: tradesReview,
    faqs: [
      {
        question: 'How much does branded workwear cost for a small trade team?',
        answer:
          'The Starter Workwear Pack (5 polos, 5 t-shirts, 2 hoodies with logo setup) has a guide price of £249–£399. The Trade Team Uniform Pack for a crew of ten, including hi-vis, guides at £699–£1,250. Send the logo and headcount for an exact quote.',
      },
      {
        question: 'Can you put our phone number and trade on the back?',
        answer:
          'Yes. A large back print with company name, trade and phone number is the most common request from trades — usually printed, with the logo embroidered on the chest of polos and jackets.',
      },
      {
        question: 'Can we reorder for new starters without paying setup again?',
        answer:
          'Yes. Once your logo is digitised the stitch file stays on record, so a single new-starter polo is priced at the garment and stitch cost only.',
      },
      {
        question: 'Do you do hi-vis?',
        answer:
          'Yes — hi-vis vests, jackets and zip hoodies can be printed or embroidered with your company name and logo. Ask about stock colours and classes when you enquire.',
      },
    ],
  },
  {
    slug: 'embroidered-uniforms-for-clinics-and-salons',
    quoteGarmentType: 'Staff Uniform / Workwear Package',
    name: 'Clinics, Beauty & Wellness',
    navLabel: 'Clinics, Beauty & Wellness',
    icon: 'corporate',
    headline: 'Embroidered Uniforms for Clinics, Salons & Wellness',
    subheadline:
      'Tunics, polos, aprons and softshells with your logo stitched cleanly — the premium finish client-facing teams need.',
    metaTitle: 'Embroidered Uniforms for Clinics, Salons & Spas | OK T-Shirt Birmingham',
    metaDescription:
      'Embroidered tunics, polos, aprons and softshells for skin clinics, salons, barbers, dental practices and wellness studios. Logo digitised once, stitched in-house in Birmingham, delivered UK-wide.',
    keywords: [
      'embroidered uniforms for clinics',
      'beauty salon uniform embroidery',
      'embroidered tunics with logo',
      'aesthetics clinic staff uniform',
      'barber shop branded clothing',
      'spa uniform embroidery UK',
    ],
    intro:
      'Clients judge a clinic or salon before treatment starts. Embroidery gives reception and treatment teams a consistent, hygienic-looking uniform that still reads as premium after months of washing.',
    audience: 'Skin and aesthetics clinics, salons, barbers, dental practices, podiatrists, massage and wellness studios',
    painPointsIntro: 'What clinic and salon owners come to us with.',
    painPoints: [
      {
        title: 'The brand feels premium, the uniform does not',
        body: 'Stitched logos on tunics and softshells match the standard of the treatment room.',
      },
      {
        title: 'Reception and treatment staff look different',
        body: 'One logo, one placement, one colour palette across polos, tunics and aprons.',
      },
      {
        title: 'Hygiene perception matters',
        body: 'Fresh, matching uniform signals a clean, organised practice.',
      },
      {
        title: 'New staff need uniform quickly',
        body: 'Top-ups are stitched from the file on record, no new setup charge.',
      },
    ],
    garmentsIntro: 'Uniform pieces that suit client-facing teams, each ready for an embroidered logo.',
    garments: [
      { handle: 'custom-embroidered-beauty-tunic', note: 'Treatment-room staple' },
      { handle: 'custom-embroidered-polo-t-shirt', note: 'Reception and front of house' },
      { handle: 'custom-embroidered-apron-with-pocket', note: 'Salon and barber floor' },
      { handle: 'custom-embroidered-softshell-jacket', note: 'Smart outer layer' },
      { handle: 'custom-embroidered-full-zip-fleece', note: 'Winter uniform' },
      { handle: 'custom-embroidered-two-tone-gilet', note: 'Layer for mobile therapists' },
    ],
    collectionHref: '/embroidery',
    collectionLabel: 'See the embroidery service',
    packageSlugs: ['premium-staff-uniform-pack'],
    decoration: [
      {
        title: 'Embroider',
        points: ['Tunics, polos, aprons and softshells', 'Small, clean logo on the chest', 'Thread colours matched to your brand'],
      },
      {
        title: 'Print',
        points: ['Promotional t-shirts for launches', 'Tote bags for retail products', 'Banners for the shopfront or events'],
      },
    ],
    proof: {
      type: 'case-study',
      slug: 'river-island-live-embroidery-bullring-birmingham',
      label: 'Embroidery trusted in a flagship retail environment',
    },
    faqs: [
      {
        question: 'Can you match thread colours to our brand?',
        answer:
          'Yes. We pick thread colours against your logo during digitising and check them on a sew-out before the run. Send a brand colour reference if you have one.',
      },
      {
        question: 'Do you embroider tunics?',
        answer:
          'Yes. The Orchid beauty tunic is stocked and priced ready for embroidery, and we can quote other tunic styles on request.',
      },
      {
        question: 'How small can the logo be on a tunic or polo?',
        answer:
          'A standard chest logo is around 8–10cm wide. Very fine text below roughly 5mm tall will not stitch cleanly, so we will flag it and suggest a simplified version before production.',
      },
      {
        question: 'What does a uniform for a team of six cost?',
        answer:
          'The Premium Staff Uniform Pack guides at £500–£1,500 depending on the mix of polos, tunics, jackets and aprons. Send the headcount and garment mix for a fixed quote.',
      },
    ],
  },
  {
    slug: 'restaurant-and-hospitality-uniforms',
    quoteGarmentType: 'Staff Uniform / Workwear Package',
    name: 'Hospitality',
    navLabel: 'Restaurants & Hospitality',
    icon: 'retail',
    headline: 'Restaurant & Hospitality Uniform Printing',
    subheadline:
      'Aprons, chef jackets, polos and caps embroidered or printed for kitchens and front-of-house teams — plus banners for the shopfront.',
    metaTitle: 'Restaurant Uniform Printing & Embroidery Birmingham | OK T-Shirt',
    metaDescription:
      'Branded aprons, chef jackets, chef hats, polos and caps for restaurants, cafés, takeaways, food trucks and hotels. Embroidered and printed in-house in Birmingham with fast turnaround and UK delivery.',
    keywords: [
      'restaurant uniform printing',
      'embroidered aprons with logo',
      'chef jacket embroidery',
      'café staff uniform',
      'takeaway branded clothing',
      'hospitality workwear Birmingham',
    ],
    intro:
      'Hospitality uniform has to survive service, look consistent on social media and arrive before the opening. We embroider aprons and chef jackets in-house and print the promotional pieces around them.',
    audience: 'Restaurants, cafés, takeaways, food trucks, hotels, bars and caterers',
    painPointsIntro: 'The problems hospitality operators bring us.',
    painPoints: [
      {
        title: 'Staff look inconsistent front of house',
        body: 'Matching embroidered aprons and polos give every shift the same look.',
      },
      {
        title: 'Aprons are needed quickly',
        body: 'Stocked aprons and in-house embroidery mean a fast turnaround, with collection from Great Barr.',
      },
      {
        title: 'The brand needs to show up in photos',
        body: 'A clean chest logo on chef jackets and caps carries through to social content.',
      },
      {
        title: 'Signage and uniform from different suppliers never match',
        body: 'We print PVC banners on the same artwork as the uniform.',
      },
    ],
    garmentsIntro: 'Kitchen and front-of-house pieces, embroidered or printed with your logo.',
    garments: [
      { handle: 'custom-embroidered-apron-with-pocket', note: 'Front-of-house favourite' },
      { handle: 'custom-embroidered-cotton-apron', note: '100% cotton, full length' },
      { handle: 'custom-embroidered-chef-jacket', note: 'Kitchen team' },
      { handle: 'custom-embroidered-chef-hat', note: 'Matching headwear' },
      { handle: 'custom-embroidered-polo-t-shirt', note: 'Bar and floor staff' },
      { handle: 'custom-embroidered-baseball-cap', note: 'Takeaways and food trucks' },
    ],
    collectionHref: '/collections/workwear',
    collectionLabel: 'View all workwear',
    packageSlugs: ['business-visibility-pack', 'premium-staff-uniform-pack'],
    decoration: [
      {
        title: 'Embroider',
        points: ['Aprons, chef jackets and caps', 'Withstands hot washes', 'Logo digitised once, reused every order'],
      },
      {
        title: 'Print',
        points: ['Promotional t-shirts and tote bags', 'PVC and mesh banners with eyelets', 'Menus and offers on stickers and labels'],
      },
    ],
    includeBanners: true,
    proof: deadlineReview,
    faqs: [
      {
        question: 'Will embroidery survive commercial laundry?',
        answer:
          'Yes. Stitched thread does not crack or peel the way a transfer eventually can, which is why aprons and chef jackets are embroidered rather than printed.',
      },
      {
        question: 'Can you print a banner to match the uniform?',
        answer:
          'Yes. PVC and mesh banners are printed in-house on a Roland TrueVIS printer from the same artwork, with eyelets included. See the banner printing page for sizes and prices.',
      },
      {
        question: 'How fast can we get aprons for a new opening?',
        answer:
          'Stocked aprons with a digitised logo are usually produced within 3–5 working days; first orders need a little longer for digitising. Tell us the opening date and we will confirm what is achievable, including same-day options where stock allows.',
      },
    ],
  },
  {
    slug: 'gym-and-sports-club-merchandise',
    quoteGarmentType: 'Staff Uniform / Workwear Package',
    name: 'Gyms, Clubs & Sports Teams',
    navLabel: 'Gyms, Clubs & Sports Teams',
    icon: 'sports',
    headline: 'Gym, Club & Sports Team Merchandise',
    subheadline:
      'Training tops, hoodies, polos, caps and banners for clubs, gyms and teams — one identity across kit and supporter merchandise.',
    metaTitle: 'Gym Merchandise & Sports Club Kit Printing | OK T-Shirt Birmingham',
    metaDescription:
      'Custom gym merchandise and sports club kit: printed training tops, embroidered polos, hoodies, caps and match-day banners. Sponsor logos handled, produced in-house in Birmingham, delivered UK-wide.',
    keywords: [
      'gym merchandise printing',
      'sports club kit printing',
      'custom club hoodies',
      'embroidered team polos',
      'football club merchandise UK',
      'boxing gym branded clothing',
    ],
    intro:
      'Clubs need kit for the team, merchandise for supporters and sponsor logos placed consistently on both. We produced exactly that for Buxton Speedway — embroidered jackets and named polos for riders, printed hoodies and caps for fans.',
    audience: 'Football clubs, boxing gyms, personal trainers, bowls clubs, dance teams, martial arts clubs and university societies',
    painPointsIntro: 'What clubs and gym owners want sorted.',
    painPoints: [
      {
        title: 'Team identity across different garments',
        body: 'The crest stays consistent from embroidered polos to printed hoodies and caps.',
      },
      {
        title: 'Merchandise that raises funds',
        body: 'Supporter hoodies, tees and caps priced per unit so the club keeps the margin.',
      },
      {
        title: 'Seasonal kit deadlines',
        body: 'Pre-season and tournament dates confirmed before production starts.',
      },
      {
        title: 'Sponsor logos in the right place every time',
        body: 'Multi-logo placements handled on a repeatable file, so every batch matches.',
      },
    ],
    garmentsIntro: 'Training and supporter pieces that hold up to use, sweat and washing.',
    garments: [
      { handle: 'custom-printed-sports-t-shirt', note: 'Training top' },
      { handle: 'custom-embroidered-sports-polo-shirt', note: 'Coaches and officials' },
      { handle: 'custom-printed-hoodie-80-20', note: 'Supporter hoodie' },
      { handle: 'custom-embroidered-snapback-cap', note: 'Merch table favourite' },
      { handle: 'custom-printed-workout-leggings', note: 'Gym and dance' },
      { handle: 'custom-printed-zipped-joggers', note: 'Travel and warm-up' },
    ],
    collectionHref: '/collections/t-shirts',
    collectionLabel: 'View all t-shirts',
    packageSlugs: ['sports-club-kit-pack'],
    decoration: [
      {
        title: 'Embroider',
        points: ['Polos, jackets and caps', 'Club crest and sponsor logos', 'Named garments for players and staff'],
      },
      {
        title: 'Print',
        points: ['Training tops and hoodies', 'Large sponsor graphics and back prints', 'Match-day and event banners'],
      },
    ],
    includeBanners: true,
    proof: {
      type: 'case-study',
      slug: 'buxton-speedway-teamwear-merchandise',
      label: 'Teamwear and merchandise for a returning speedway club',
    },
    faqs: [
      {
        question: 'Can you add individual names and numbers?',
        answer:
          'Yes. Named polos and jackets are embroidered individually, and printed numbers and names are added to training tops and hoodies at the same time as the club design.',
      },
      {
        question: 'Can members order their own merchandise?',
        answer:
          'For most clubs we produce the range in one run so the club controls pricing and margin. If you need something different, tell us how the club wants to sell it and we will suggest a route.',
      },
      {
        question: 'How much does a club kit order cost?',
        answer:
          'The Sports Club Kit Pack guides at £750–£3,000 depending on garments, quantities and how many sponsor positions each piece carries. Send the squad size and the logos for a fixed quote.',
      },
      {
        question: 'Do you handle sponsor logos?',
        answer:
          'Yes. Each sponsor logo is set up once and placed in the agreed position on every garment, whether embroidered on polos or printed on hoodies and tees.',
      },
    ],
  },
  {
    slug: 'corporate-uniforms-and-branded-merchandise',
    quoteGarmentType: 'Staff Uniform / Workwear Package',
    name: 'Corporate & Office',
    navLabel: 'Corporate & Office',
    icon: 'community',
    headline: 'Corporate Uniforms & Branded Merchandise',
    subheadline:
      'Smart embroidered polos, softshells and jackets for offices, agencies and events, with tote bags and merchandise to match.',
    metaTitle: 'Corporate Uniforms & Branded Merchandise | OK T-Shirt Birmingham',
    metaDescription:
      'Embroidered polos, softshell jackets and gilets for estate agents, accountants, recruiters and training providers, plus printed tote bags and event merchandise. Produced in-house in Birmingham, UK-wide delivery.',
    keywords: [
      'corporate uniforms Birmingham',
      'embroidered company polo shirts',
      'branded softshell jackets',
      'corporate merchandise UK',
      'estate agent uniform',
      'exhibition staff clothing',
    ],
    intro:
      'Office teams rarely need a full uniform — they need smart, consistent pieces for viewings, open days, exhibitions and client visits, plus merchandise that carries the brand into the room.',
    audience: 'Estate agents, accountants, recruitment firms, training providers, agencies and professional services',
    painPointsIntro: 'The requests we get from office-based teams.',
    painPoints: [
      {
        title: 'Event and exhibition uniform',
        body: 'Matching polos or softshells for the stand, ready before the event date.',
      },
      {
        title: 'Smart teamwear for client-facing staff',
        body: 'Embroidered logos on quality garments that look right in an office or on a viewing.',
      },
      {
        title: 'Merchandise for open days and onboarding',
        body: 'Printed tote bags, caps and t-shirts from the same artwork as the uniform.',
      },
      {
        title: 'A supplier who keeps the files',
        body: 'Reorders for new hires without re-sending artwork or paying setup again.',
      },
    ],
    garmentsIntro: 'Smart pieces for offices and events, priced per unit with bulk pricing from 10.',
    garments: [
      { handle: 'custom-embroidered-polo-t-shirt', note: 'Office and events' },
      { handle: 'custom-embroidered-softshell-jacket', note: 'Smart outer layer' },
      { handle: 'custom-embroidered-bodywarmer-gilet', note: 'Viewings and site visits' },
      { handle: 'custom-embroidered-full-zip-fleece', note: 'Winter teamwear' },
      { handle: 'custom-printed-tote-bag', note: 'Open days and welcome packs' },
      { handle: 'custom-embroidered-baseball-cap', note: 'Outdoor events' },
    ],
    collectionHref: '/collections/jackets-gilets',
    collectionLabel: 'View jackets and gilets',
    packageSlugs: ['premium-staff-uniform-pack', 'business-visibility-pack'],
    decoration: [
      {
        title: 'Embroider',
        points: ['Polos, softshells, gilets and fleeces', 'Understated chest logo', 'Premium, lasting finish'],
      },
      {
        title: 'Print',
        points: ['Tote bags and event t-shirts', 'Exhibition and open-day banners', 'Stickers and labels for welcome packs'],
      },
    ],
    includeBanners: true,
    proof: {
      type: 'case-study',
      slug: 'river-island-live-embroidery-bullring-birmingham',
      label: 'Trusted by a national retailer for a flagship-store activation',
    },
    faqs: [
      {
        question: 'Can we order a mix of garments in one uniform order?',
        answer:
          'Yes. Most corporate orders mix polos, softshells and gilets. The logo is digitised once and used across every garment, so mixed orders do not add setup cost.',
      },
      {
        question: 'Do you supply merchandise as well as uniform?',
        answer:
          'Yes — printed tote bags, caps and t-shirts, and PVC banners for exhibitions and open days, all from the same artwork. The Business Visibility Pack bundles uniform, a banner and caps or tote bags.',
      },
      {
        question: 'Can you deliver to several offices?',
        answer:
          'Yes. We deliver to any UK postcode and can split an order across addresses; tell us the breakdown when you request the quote.',
      },
    ],
  },
  {
    slug: 'event-t-shirts-and-merchandise',
    quoteGarmentType: 'T-Shirts',
    name: 'Events & Community',
    navLabel: 'Events & Community',
    icon: 'celebration',
    headline: 'Event T-Shirts & Merchandise',
    subheadline:
      'Printed t-shirts, hoodies, tote bags and banners for churches, charities, conferences, festivals and stag or hen groups — produced to a fixed date.',
    metaTitle: 'Event T-Shirt Printing & Merchandise Birmingham | OK T-Shirt',
    metaDescription:
      'Event t-shirts, hoodies, caps, tote bags and banners printed in-house in Birmingham for churches, charities, conferences, festivals, corporate events and stag or hen groups. Fixed-date production, UK delivery.',
    keywords: [
      'event t-shirt printing',
      'charity t-shirts printed',
      'conference merchandise UK',
      'church group hoodies',
      'stag do t-shirts Birmingham',
      'festival crew t-shirts',
    ],
    intro:
      'Events have one thing in common: the date does not move. We confirm the schedule before production, help with artwork and group sizing, and print t-shirts, hoodies, tote bags and banners under one roof.',
    audience: 'Churches, charities, conferences, corporate events, festivals, community groups and stag or hen parties',
    painPointsIntro: 'What event organisers need from a printer.',
    painPoints: [
      {
        title: 'A fixed deadline',
        body: 'We confirm a realistic production date before you commit, with express options for tight turnarounds.',
      },
      {
        title: 'Design help',
        body: 'Send a sketch or an idea and our design team prepares print-ready artwork for a small fee.',
      },
      {
        title: 'Group sizing',
        body: 'Mixed sizes across one design are standard — collect sizes once and send the list.',
      },
      {
        title: 'Collection or delivery',
        body: 'Free collection from Great Barr, or delivery to the venue or organiser anywhere in the UK.',
      },
    ],
    garmentsIntro: 'Event staples printed in full colour, with bulk pricing from 10 units.',
    garments: [
      { handle: 'custom-printed-round-neck-t-shirt', note: 'The event t-shirt' },
      { handle: 'custom-printed-hoodie-80-20', note: 'Crew and volunteer hoodie' },
      { handle: 'custom-printed-sweatshirt', note: 'Cooler-weather option' },
      { handle: 'custom-printed-tote-bag', note: 'Delegate and goody bags' },
      { handle: 'custom-embroidered-snapback-cap', note: 'Crew caps' },
      { handle: 'custom-printed-long-sleeve-t-shirt', note: 'Outdoor events' },
    ],
    collectionHref: '/collections/t-shirts',
    collectionLabel: 'View all t-shirts',
    packageSlugs: ['event-merchandise-pack'],
    decoration: [
      {
        title: 'Print',
        points: ['T-shirts, hoodies and tote bags', 'Full-colour artwork, names and dates', 'DTF for dark garments and mixed fabrics'],
      },
      {
        title: 'Embroider',
        points: ['Caps and crew polos', 'Charity and church logos', 'Keepsake pieces that last'],
      },
    ],
    includeBanners: true,
    proof: {
      type: 'case-study',
      slug: 'river-island-live-embroidery-bullring-birmingham',
      label: 'Live embroidery delivered inside a flagship store event',
    },
    faqs: [
      {
        question: 'How late can we order for an event?',
        answer:
          'Standard production is 3–5 working days from artwork approval. Urgent jobs can often be turned around faster where stock allows — contact us with the date before ordering and we will tell you honestly what is possible.',
      },
      {
        question: 'Can everyone have a different name or size?',
        answer:
          'Yes. Mixed sizes are standard, and individual names or numbers can be printed alongside the shared event design.',
      },
      {
        question: 'Do you print banners for the venue?',
        answer:
          'Yes. PVC and mesh banners are printed in-house with eyelets included, so the stage, stall or registration desk matches the t-shirts.',
      },
      {
        question: 'What does an event pack cost?',
        answer:
          'The Event Merchandise Pack (t-shirts, hoodies, caps, tote bags and a banner) guides at £300–£2,000 depending on quantities. Send the headcount and the artwork for a fixed quote.',
      },
    ],
  },
];

export function getIndustryBySlug(slug: string): B2bIndustry | undefined {
  return b2bIndustries.find((industry) => industry.slug === slug);
}

export function getIndustryPath(industry: Pick<B2bIndustry, 'slug'>): string {
  return `/${industry.slug}`;
}

/** Entry point tiles for the hub and the homepage — industries plus the clothing-brand pillar. */
export const b2bAudienceTiles: { title: string; description: string; href: string; icon: LocationIconName }[] = [
  ...b2bIndustries.map((industry) => ({
    title: industry.name,
    description: industry.subheadline,
    href: getIndustryPath(industry),
    icon: industry.icon,
  })),
  {
    title: 'Clothing Brands',
    description:
      'Samples, bulk print runs and reorders for streetwear founders and new clothing brands — with a route to an online store.',
    href: CLOTHING_BRAND_PATH,
    icon: 'retail',
  },
];

export const QUOTE_PATH = '/quote';

/** /quote link that pre-fills the form with a package (type + contents message). */
export function buildPackQuoteHref(pack: Pick<B2bPackage, 'slug'>): string {
  return `${QUOTE_PATH}?pack=${pack.slug}`;
}

/** /quote link that pre-selects one "What do you need?" option. */
export function buildTypeQuoteHref(garmentType: string): string {
  return `${QUOTE_PATH}?type=${encodeURIComponent(garmentType)}`;
}
